/**
 * The Personnel rates tab of /downhole/rates (api#1057, dh_personnel_rates
 * 0514): what each Directional & MWD crew role bills a customer per day.
 * Customer billing only; pay is a later batch.
 *
 * ONE ROW prices ONE role for one customer, or for everybody when
 * `customer_id` is null (the company default), over a range of calendar days
 * (both ends inclusive; no expiry is open-ended). It quotes up to three day
 * rates: operating, standby and travel. Standby is its own rate, not a
 * percentage. A figure left out is NOT QUOTED, not $0, so the register says
 * "Not quoted", as the tool rate card does.
 *
 * Two live rows for one role and customer may not overlap in time: the API
 * answers 409 RATE_OVERLAP naming the row in the way, and the register
 * highlights it. Delete is soft and reversible; a restore can overlap too.
 *
 * ROLES: read admin, office_staff, dispatcher; write admin, office_staff
 * (the tool rate card's, lib/downhole-roles.ts). The page that hosts this tab
 * has already checked read. Behind the dd_mwd mode: the host shows the tab
 * only when the mode is on.
 */
import React, { useMemo, useState } from 'react';
import { ArchiveRestore, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../api/client';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  useCreatePersonnelRate,
  useDeletePersonnelRate,
  useDownholePersonnelRates,
  useRestorePersonnelRate,
  useUpdatePersonnelRate,
} from '../../hooks/useDownholePersonnelRates';
import { formatCalendarDate } from '../../lib/calendar-date';
import { fmtCurrency } from '../../lib/display-labels';
import { DH_PERSONNEL_ROLE_LABEL, dhLabel } from '../../lib/downhole-labels';
import { onlineOrSay } from '../../lib/downhole-offline';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DH_PERSONNEL_ROLES, type DhPersonnelRate, type DhPersonnelRateWrite } from '../../types/downhole';
import { DH_DATE_INPUT, DH_INPUT, DH_SECONDARY, DhDialog, DhEmpty, DhField, DhPanel } from './DownholeBits';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50';
const DANGER =
  'inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink disabled:opacity-50';

export const PERSONNEL_RATE_FIELDS = ['day_rate', 'standby_day_rate', 'travel_day_rate'] as const;
type RateField = (typeof PERSONNEL_RATE_FIELDS)[number];
const RATE_LABEL: Record<RateField, string> = { day_rate: 'Day rate', standby_day_rate: 'Standby day', travel_day_rate: 'Travel day' };

/** "Not quoted" is a fact about the rate, so it is said, not dashed. */
function Money({ value, currency }: { value: number | null; currency: string }) {
  if (value === null || value === undefined) return <span className="text-xs text-fieldiq-muted">Not quoted</span>;
  return <>{fmtCurrency(value, currency, 2)}</>;
}

const dayRange = (r: DhPersonnelRate) =>
  `${formatCalendarDate(r.effective_date) ?? '—'} – ${r.expiry_date ? formatCalendarDate(r.expiry_date) ?? '—' : 'onward'}`;

export function PersonnelRatesTab({
  canWrite,
  customers,
  tenantCurrency,
  today,
}: {
  canWrite: boolean;
  customers: { id: string; name: string }[];
  tenantCurrency: string | null;
  today: string | null;
}) {
  const [customer, setCustomer] = useState('');
  const [role, setRole] = useState('');
  const [on, setOn] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<DhPersonnelRate | 'new' | null>(null);
  const [deleting, setDeleting] = useState<DhPersonnelRate | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);

  // Customer, role and the day go to the API. "Company default" (customer_id
  // null) has no filter on the route, so it is applied over the answer.
  const params = useMemo(
    () => ({
      ...(customer && customer !== 'default' && { customer_id: customer }),
      ...(role && { personnel_role: role }),
      ...(/^\d{4}-\d{2}-\d{2}$/.test(on) && { on }),
      ...(showDeleted && { include_deleted: 'true' as const }),
    }),
    [customer, role, on, showDeleted],
  );
  const query = useDownholePersonnelRates(params, true);
  const restore = useRestorePersonnelRate();
  const failure = queryLoadFailure(query);
  const rows = (query.data?.data ?? []).filter((r) => customer !== 'default' || r.customer_id === null);
  const filtered = !!(customer || role || on);
  const currency = query.data?.meta.currency ?? tenantCurrency;
  const day = query.data?.meta.today ?? today;

  const doRestore = (r: DhPersonnelRate) => {
    if (!onlineOrSay(toast.error)) return;
    restore.mutate(r.id, {
      onSuccess: () => toast.success('Rate restored'),
      onError: (error) => {
        toast.error(getApiErrorMessage(error, 'The rate was not restored.'));
        const id = (error as { response?: { data?: { conflicting_rate_id?: string } } })?.response?.data?.conflicting_rate_id;
        if (id) setHighlight(id);
      },
    });
  };

  return (
    <div className="space-y-4" data-testid="personnel-rates-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <DhField label="Customer">
            <select className={DH_INPUT} value={customer} onChange={(e) => setCustomer(e.target.value)} aria-label="Customer">
              <option value="">Every customer and the default</option>
              <option value="default">Company default only</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </DhField>
        </div>
        <div className="w-56">
          <DhField label="Role">
            <select className={DH_INPUT} value={role} onChange={(e) => setRole(e.target.value)} aria-label="Role">
              <option value="">All roles</option>
              {DH_PERSONNEL_ROLES.map((r) => <option key={r} value={r}>{DH_PERSONNEL_ROLE_LABEL[r]}</option>)}
            </select>
          </DhField>
        </div>
        <div className="w-44">
          <DhField label="In effect on">
            <input type="date" className={DH_DATE_INPUT} value={on} onChange={(e) => setOn(e.target.value)} aria-label="In effect on" />
          </DhField>
        </div>
        <label className="flex h-10 items-center gap-2 text-sm text-fieldiq-text">
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
          Show deleted
        </label>
      </div>

      <DhPanel
        eyebrow="Crew rate card"
        title="Personnel rates"
        testId="personnel-rate-register"
        actions={
          canWrite && query.data ? (
            <button type="button" className={DH_SECONDARY} onClick={() => setEditing('new')}>
              <Plus className="size-4" aria-hidden="true" />
              Add personnel rate
            </button>
          ) : undefined
        }
        footer={
          query.data && !failure && rows.length > 0
            ? `${rows.length} ${rows.length === 1 ? 'rate' : 'rates'}${filtered ? ' match these filters' : ''}. A customer's own rate comes before the company default.`
            : undefined
        }
      >
        {query.isLoading || (!failure && !query.data) ? (
          <div className="flex justify-center py-16">
            <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading personnel rates" />
          </div>
        ) : failure ? (
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="personnel rates" />
        ) : rows.length === 0 ? (
          <DhEmpty>{filtered ? 'No personnel rate matches these filters.' : 'There are no personnel rates yet.'}</DhEmpty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-left">
              <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                <tr>
                  <th scope="col" className="px-4 py-3">Role</th>
                  <th scope="col" className="px-4 py-3">Customer</th>
                  {PERSONNEL_RATE_FIELDS.map((k) => <th key={k} scope="col" className="px-4 py-3 text-right">{RATE_LABEL[k]}</th>)}
                  <th scope="col" className="px-4 py-3">In effect</th>
                  {canWrite && <th scope="col" className="px-4 py-3 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    data-rate-id={r.id}
                    className={`border-t border-fieldiq-border ${highlight === r.id ? 'bg-fi-amber/10' : ''} ${r.deleted_at ? 'opacity-60' : ''}`}
                  >
                    <td className="px-4 py-3 text-sm font-bold text-white">
                      {dhLabel(DH_PERSONNEL_ROLE_LABEL, r.personnel_role)}
                      {r.deleted_at && (
                        <span className="ml-2 rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink">
                          Deleted
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-fieldiq-text">
                      {r.customer_id ? r.customer_name || '—' : <span className="text-fieldiq-muted">Company default</span>}
                    </td>
                    {PERSONNEL_RATE_FIELDS.map((k) => (
                      <td key={k} className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">
                        <Money value={r[k]} currency={r.currency} />
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">
                      {dayRange(r)}
                      <span className="ml-1 text-xs text-fieldiq-muted">{r.currency}</span>
                    </td>
                    {canWrite && (
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        {r.deleted_at ? (
                          <button type="button" className={DH_SECONDARY} disabled={restore.isPending} onClick={() => doRestore(r)}>
                            <ArchiveRestore className="size-4" aria-hidden="true" />
                            Restore
                          </button>
                        ) : (
                          <span className="inline-flex gap-2">
                            <button type="button" className={DH_SECONDARY} onClick={() => setEditing(r)} aria-label={`Edit ${dhLabel(DH_PERSONNEL_ROLE_LABEL, r.personnel_role)} rate`}>
                              <Pencil className="size-4" aria-hidden="true" />
                              Edit
                            </button>
                            <button type="button" className={DH_SECONDARY} onClick={() => setDeleting(r)} aria-label={`Delete ${dhLabel(DH_PERSONNEL_ROLE_LABEL, r.personnel_role)} rate`}>
                              <Trash2 className="size-4" aria-hidden="true" />
                            </button>
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DhPanel>

      {canWrite && editing && (
        <PersonnelRateDialog
          rate={editing === 'new' ? null : editing}
          customers={customers}
          tenantCurrency={currency}
          today={day}
          onClose={() => setEditing(null)}
          onConflict={(id) => {
            setHighlight(id);
            setShowDeleted(false);
          }}
        />
      )}
      {canWrite && deleting && <DeletePersonnelRateDialog rate={deleting} onClose={() => setDeleting(null)} />}
    </div>
  );
}

type Values = Record<'personnel_role' | 'customer_id' | 'currency' | 'effective_date' | 'expiry_date' | 'notes' | RateField, string>;

function valuesOf(r: DhPersonnelRate | null, tenantCurrency: string | null, today: string | null): Values {
  const amount = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n));
  return {
    personnel_role: r?.personnel_role ?? '',
    customer_id: r?.customer_id ?? '',
    currency: r?.currency ?? (tenantCurrency === 'USD' || tenantCurrency === 'CAD' ? tenantCurrency : 'CAD'),
    effective_date: r?.effective_date ?? today ?? '',
    expiry_date: r?.expiry_date ?? '',
    notes: r?.notes ?? '',
    day_rate: amount(r?.day_rate),
    standby_day_rate: amount(r?.standby_day_rate),
    travel_day_rate: amount(r?.travel_day_rate),
  };
}

const MONEY = /^\d+(\.\d+)?$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * What to send, or the first problem to say. A create sends every filled
 * field; an edit only what moved, a cleared field as null. An amount is sent
 * as a number; a blank amount is "not quoted" (null), never 0.
 */
export function personnelRateBody(v: Values, rate: DhPersonnelRate | null, initial: Values): { body: DhPersonnelRateWrite; problem: string | null } {
  if (!v.personnel_role) return { body: {}, problem: 'Choose the role this rate prices.' };
  if (!DAY.test(v.effective_date)) return { body: {}, problem: 'Choose the day the rate starts.' };
  if (v.expiry_date && v.expiry_date < v.effective_date) return { body: {}, problem: "The expiry date can't be before the effective date." };
  for (const k of PERSONNEL_RATE_FIELDS) {
    if (v[k].trim() && !MONEY.test(v[k].trim())) return { body: {}, problem: `${RATE_LABEL[k]} must be an amount without separators.` };
  }
  if (PERSONNEL_RATE_FIELDS.every((k) => !v[k].trim())) {
    return { body: {}, problem: 'Enter at least one price: day rate, standby day rate or travel day rate.' };
  }
  const body: DhPersonnelRateWrite = {};
  const changed = (k: keyof Values) => !rate || v[k] !== initial[k];
  if (changed('personnel_role')) body.personnel_role = v.personnel_role;
  if (changed('customer_id')) {
    if (rate) body.customer_id = v.customer_id || null;
    else if (v.customer_id) body.customer_id = v.customer_id;
  }
  if (changed('currency')) body.currency = v.currency as 'CAD' | 'USD';
  if (changed('effective_date')) body.effective_date = v.effective_date;
  if (changed('expiry_date') && (rate || v.expiry_date)) body.expiry_date = v.expiry_date || null;
  if (changed('notes') && (rate || v.notes.trim())) body.notes = v.notes.trim() || null;
  for (const k of PERSONNEL_RATE_FIELDS) {
    if (!changed(k)) continue;
    const raw = v[k].trim();
    if (raw === '' && !rate) continue;
    body[k] = raw === '' ? null : Number(raw);
  }
  return { body, problem: null };
}

function PersonnelRateDialog({
  rate,
  customers,
  tenantCurrency,
  today,
  onClose,
  onConflict,
}: {
  rate: DhPersonnelRate | null;
  customers: { id: string; name: string }[];
  tenantCurrency: string | null;
  today: string | null;
  onClose: () => void;
  onConflict: (id: string) => void;
}) {
  const create = useCreatePersonnelRate();
  const update = useUpdatePersonnelRate();
  const initial = useMemo(() => valuesOf(rate, tenantCurrency, today), [rate, tenantCurrency, today]);
  const [v, setV] = useState<Values>(initial);
  const [error, setError] = useState<string | null>(null);
  const busy = create.isPending || update.isPending;
  const set = (k: keyof Values) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setV((all) => ({ ...all, [k]: e.target.value }));

  const submit = () => {
    setError(null);
    const { body, problem } = personnelRateBody(v, rate, initial);
    if (problem) return setError(problem);
    if (rate && Object.keys(body).length === 0) return onClose();
    if (!onlineOrSay(setError)) return;
    const done = {
      onSuccess: () => {
        toast.success(rate ? 'Personnel rate saved' : 'Personnel rate added');
        onClose();
      },
      onError: (err: unknown) => {
        setError(getApiErrorMessage(err, rate ? 'The rate was not saved.' : 'The rate was not added.'));
        const data = (err as { response?: { data?: { code?: string; conflicting_rate_id?: string } } })?.response?.data;
        if (data?.code === 'RATE_OVERLAP' && data.conflicting_rate_id) onConflict(data.conflicting_rate_id);
      },
    };
    if (rate) update.mutate({ id: rate.id, body }, done);
    else create.mutate(body, done);
  };

  return (
    <DhDialog
      open
      wide
      title={rate ? 'Edit personnel rate' : 'Add personnel rate'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className={PRIMARY} onClick={submit} disabled={busy}>
            {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {rate ? 'Save' : 'Add rate'}
          </button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <DhField label="Role *">
          <select className={DH_INPUT} value={v.personnel_role} onChange={set('personnel_role')} aria-label="Role">
            <option value="">Choose a role</option>
            {DH_PERSONNEL_ROLES.map((r) => <option key={r} value={r}>{DH_PERSONNEL_ROLE_LABEL[r]}</option>)}
          </select>
        </DhField>
        <DhField label="Customer" hint="Leave as the company default to price every customer without a rate of their own.">
          <select className={DH_INPUT} value={v.customer_id} onChange={set('customer_id')} aria-label="Customer">
            <option value="">Company default</option>
            {rate?.customer_id && !customers.some((c) => c.id === rate.customer_id) && (
              <option value={rate.customer_id}>{rate.customer_name || 'Current customer'}</option>
            )}
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </DhField>
        {PERSONNEL_RATE_FIELDS.map((k) => (
          <DhField key={k} label={RATE_LABEL[k]} hint={k === 'standby_day_rate' ? 'Its own rate, not a share of the day rate.' : undefined}>
            <input className={DH_INPUT} inputMode="decimal" value={v[k]} onChange={set(k)} placeholder="Not quoted" aria-label={RATE_LABEL[k]} />
          </DhField>
        ))}
        <DhField label="Currency">
          <select className={DH_INPUT} value={v.currency} onChange={set('currency')} aria-label="Currency">
            <option value="CAD">CAD</option>
            <option value="USD">USD</option>
          </select>
        </DhField>
        <DhField label="Effective *">
          <input type="date" className={DH_DATE_INPUT} value={v.effective_date} onChange={set('effective_date')} aria-label="Effective" />
        </DhField>
        <DhField label="Expires" hint="Leave blank for no end date.">
          <input type="date" className={DH_DATE_INPUT} value={v.expiry_date} onChange={set('expiry_date')} aria-label="Expires" />
        </DhField>
      </div>
      <div className="mt-3">
        <DhField label="Notes">
          <textarea className={`${DH_INPUT} h-20 py-2`} value={v.notes} onChange={set('notes')} maxLength={10000} aria-label="Notes" />
        </DhField>
      </div>
      {error && (
        <p role="alert" className="mt-3 rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
          {error}
        </p>
      )}
    </DhDialog>
  );
}

function DeletePersonnelRateDialog({ rate, onClose }: { rate: DhPersonnelRate; onClose: () => void }) {
  const remove = useDeletePersonnelRate();
  const submit = () => {
    if (!onlineOrSay(toast.error)) return;
    remove.mutate(rate.id, {
      onSuccess: () => {
        toast.success('Personnel rate deleted');
        onClose();
      },
      onError: (error) => toast.error(getApiErrorMessage(error, 'The rate was not deleted.')),
    });
  };
  return (
    <DhDialog
      open
      title="Delete this personnel rate?"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>Cancel</button>
          <button type="button" className={DANGER} disabled={remove.isPending} onClick={submit}>Delete rate</button>
        </>
      }
    >
      <p className="text-sm text-fieldiq-text">
        {dhLabel(DH_PERSONNEL_ROLE_LABEL, rate.personnel_role)} for {rate.customer_id ? rate.customer_name : 'the company default'}, {dayRange(rate)}.
        It can be restored with Show deleted.
      </p>
    </DhDialog>
  );
}
